import moment from "moment";
import { useEffect, useState } from "react";
import profile from "../../../images/profileAvatar.jpg";
import tankTop from "../../../images/tank-top.png";
import JugadorSeasonHighsPanel from "./JugadorSeasonHighsPanel";

function StatCard({ label, value }) {
    return (
        <div className="bg-[#102216] border border-white/10 p-3 sm:p-4 rounded-xl text-center">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">{label}</span>
            <span className="text-2xl sm:text-3xl lg:text-4xl font-black text-green-400">{value}</span>
        </div>
    );
}

function AchievementBadge({ label, tone = "gold" }) {
    const tones = {
        gold: "bg-yellow-300/90 text-[#2d2211] border-yellow-500/60",
        silver: "bg-slate-200 text-slate-800 border-slate-300",
        bronze: "bg-amber-400/70 text-[#3d2b1f] border-amber-700/50",
    };

    return (
        <span className={`rounded px-3 py-1 text-[10px] font-black uppercase tracking-wide border ${tones[tone]}`}>
            {label}
        </span>
    );
}

function JerseyNumber({ number, isCurrent }) {
    const jerseyTone = isCurrent ? "opacity-100" : "opacity-55";

    return (
        <div className="relative w-14 h-14 flex items-center justify-center">
                <img src={tankTop} alt="Camiseta" className={`w-14 h-14 object-contain ${jerseyTone}`} />
                <span className="absolute inset-0 flex items-center justify-center text-xl font-black text-white pt-1">
                    {number}
                </span>
        </div>
    );
}

export default function JugadorHeroSection({
    displayName,
    principalProfile,
    jugadorProfile,
    dtProfile,
    imageUrl,
    careerProm,
    dtCareerTotals,
    highs,
    mode = "jugador",
}) {
    const [hasImageError, setHasImageError] = useState(false);
    const isTecnicoMode = mode === "tecnico";
    const activeProfile = isTecnicoMode ? dtProfile : jugadorProfile;
    const torneosList = activeProfile?.torneos || [];
    const torneos = torneosList.map((torneo) => torneo.liga).join(", ");
    const torneosCount = torneosList.length;
    const nacimiento = activeProfile?.nacimiento
        ? moment(activeProfile.nacimiento).format("DD/MM/YYYY")
        : "-";
    const edad = activeProfile?.nacimiento
        ? ` (${moment().diff(moment(activeProfile.nacimiento), "years")})`
        : "";
    const debut = activeProfile?.debut ? moment(activeProfile.debut).format("DD/MM/YYYY") : "-";
    const nacionalidad =
        activeProfile?.nacionalidad ||
        activeProfile?.pais ||
        activeProfile?.nacion ||
        activeProfile?.nationality ||
        "-";
    const campeonatos = activeProfile?.campeon || [];
    const subcampeonatos = activeProfile?.segundo || [];
    const tercerosPuestos = activeProfile?.tercero || [];
    const hasAchievements = campeonatos.length > 0 || subcampeonatos.length > 0 || tercerosPuestos.length > 0;
    const shouldUseFallbackImage = Boolean(principalProfile?.pendImage) || hasImageError;

    useEffect(() => {
        setHasImageError(false);
    }, [imageUrl, principalProfile?.pendImage, displayName]);

    return (
        <section className="mb-10 grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5 xl:col-span-4">
                <div className="relative aspect-[4/5] w-full max-w-xs mx-auto sm:max-w-sm lg:max-w-none overflow-hidden rounded-xl bg-[#102216] border border-white/10">
                    <img
                        src={shouldUseFallbackImage ? profile : imageUrl}
                        alt={displayName}
                        onError={() => {
                            setHasImageError(true);
                        }}
                        className="h-full w-full object-cover"
                    />
                </div>
            </div>

            <div className="flex flex-col justify-start lg:col-span-7 xl:col-span-8">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                    {isTecnicoMode ? (
                        <>
                            <StatCard label="Partidos" value={dtCareerTotals?.partidos ?? 0} />
                            <StatCard label="Ganados" value={dtCareerTotals?.ganados ?? 0} />
                            <StatCard label="Perdidos" value={dtCareerTotals?.perdidos ?? 0} />
                            <StatCard label="Gan %" value={dtCareerTotals?.winPerc ?? "0%"} />
                        </>
                    ) : (
                        <>
                            <StatCard label="Puntos por partido" value={(careerProm?.ppg ?? 0).toFixed(1)} />
                            <StatCard label="Asistencias por partido" value={(careerProm?.apg ?? 0).toFixed(1)} />
                            <StatCard label="Rebotes por partido" value={(careerProm?.rtpg ?? 0).toFixed(1)} />
                            <StatCard label="Partidos" value={jugadorProfile?.carrera?.partidos ?? 0} />
                        </>
                    )}
                </div>

                <div className={`grid grid-cols-1 ${isTecnicoMode ? "" : "lg:grid-cols-2"} gap-6 sm:gap-8`}>
                    <div className="space-y-5 sm:space-y-6 rounded-xl border border-white/10 bg-[#102216] p-4 sm:p-5">
                        {hasAchievements ? <div className="flex flex-wrap items-center gap-2">
                            {campeonatos.map((item, index) => (
                                <AchievementBadge key={`campeon-${item}-${index}`} label={`Campeón ${item}`} tone="gold" />
                            ))}
                            {subcampeonatos.map((item, index) => (
                                <AchievementBadge key={`segundo-${item}-${index}`} label={`2do ${item}`} tone="silver" />
                            ))}
                            {tercerosPuestos.map((item, index) => (
                                <AchievementBadge key={`tercero-${item}-${index}`} label={`3ro ${item}`} tone="bronze" />
                            ))}
                        </div> : null}

                        <div className="grid grid-cols-2 gap-y-4 text-slate-300">
                            {!isTecnicoMode ? (
                                <div>
                                    <span className="text-xs uppercase text-slate-500 block font-bold tracking-wider">Altura</span>
                                    <span className="text-base sm:text-lg font-semibold">{jugadorProfile?.altura || "-"}</span>
                                </div>
                            ) : (
                                <div>
                                    <span className="text-xs uppercase text-slate-500 block font-bold tracking-wider">Debut</span>
                                    <span className="text-base sm:text-lg font-semibold">{debut}</span>
                                </div>
                            )}
                            <div>
                                <span className="text-xs uppercase text-slate-500 block font-bold tracking-wider">Nacimiento / Edad</span>
                                <span className="text-base sm:text-lg font-semibold">{nacimiento}{edad}</span>
                            </div>
                            {!isTecnicoMode ? (
                                <div>
                                    <span className="text-xs uppercase text-slate-500 block font-bold tracking-wider">Debut</span>
                                    <span className="text-base sm:text-lg font-semibold">{debut}</span>
                                </div>
                            ) : null}
                            <div>
                                <span className="text-xs uppercase text-slate-500 block font-bold tracking-wider">Nacionalidad</span>
                                <span className="text-base sm:text-lg font-semibold">{nacionalidad}</span>
                            </div>
                            <div className="col-span-2">
                                <span className="text-xs uppercase text-slate-500 block font-bold tracking-wider">Torneos ({torneosCount})</span>
                                <span className="text-sm font-medium">{torneos || "-"}</span>
                            </div>
                        </div>

                        {!isTecnicoMode ? (
                            <div className="">
                                <h4 className="text-xs uppercase text-slate-500 font-bold mb-3 tracking-wider">Números de Camiseta</h4>
                                <div className="flex gap-4 flex-wrap">
                                    {(jugadorProfile?.numero || []).map((number, index) => (
                                        <JerseyNumber key={`numero-${number}-${index}`} number={number} isCurrent={index === 0} />
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {!isTecnicoMode ? <JugadorSeasonHighsPanel highs={highs} className="mb-0" /> : null}
                </div>
            </div>
        </section>
    );
}
